import React, { useMemo, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Upload } from 'lucide-react';

interface PhotoUploadZoneProps {
  disabled?: boolean;
  isUploading?: boolean;
  onUpload: (files: File[]) => Promise<void> | void;
}

export const PhotoUploadZone: React.FC<PhotoUploadZoneProps> = ({
  disabled = false,
  isUploading = false,
  onUpload,
}) => {
  const [error, setError] = useState<string | null>(null);

  const accept = useMemo(() => 'image/*,.heic,.heif', []);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    try {
      await onUpload(Array.from(files));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    }
  };

  return (
    <div className="rounded-lg border border-dashed p-4 bg-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Add Photos</p>
          <p className="text-xs text-muted-foreground">
            Use your camera or upload from desktop. HEIC files are supported.
          </p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" disabled={disabled || isUploading}>
            <label className="cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'Uploading...' : 'Upload Files'}
              <input
                type="file"
                multiple
                accept={accept}
                capture="environment"
                className="hidden"
                onChange={(event) => {
                  void handleFiles(event.target.files);
                  event.currentTarget.value = '';
                }}
              />
            </label>
          </Button>
        </div>
      </div>
    </div>
  );
};
